import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageSection } from '../../../../../components.js';
import { AutoComplete, Button, Checkbox, DatePicker, Input, Select, Skeleton, Space } from 'antd';
import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { ClientInfo } from '../../components/client-info';
import { BDO, Client, PaymentType, PriceType } from '../../../../../types';
import SearchSelector from '../../../../../components/searchSelector';
import SearchSelectorNew from '../../../../../components/searchSelectorNew';
import { useAddresses, useClients } from '../../../../../reducers/api';
import { pushWithResult } from 'utils/intent-manager';
import moment from 'moment';

const wasteTypes = ['Mieszany', 'Gruz', 'Drewno', 'Styropian', 'Papa'] as const;
type WasteType = (typeof wasteTypes)[number];

export interface FormData {
	client?: Client;
	bdo?: BDO;
	wasteType?: WasteType;
	address?: string;
	contact?: string;
	count?: string;
	onPlace?: boolean;
	price?: string;
	priceType?: PriceType;
	wantedAdvance?: string | null;
	wantedPaymentType?: PaymentType;
	comments?: string;
	claimingDate?: moment.Moment;
	deliveryDate?: moment.Moment;
	isNewAddress?: boolean;
}

interface Props {
	select: React.ReactNode;
	value: FormData;
	onChange: (data: FormData) => void;
}

export const NewBigBagForm: React.FC<Props> = ({ select, value, onChange }) => {
	const navigate = useNavigate();

	const clients = useClients({});
	const addresses = useAddresses(value.client?.id);

	if (!clients) {
		return <Skeleton active />;
	}

	const onClientChange = (clientId: number) => {
		const client = clients.find((client) => client.id === clientId);
		if (!client) {
			return;
		}

		onChange({
			...value,
			client,
		});
	};

	const onBdoChange = (bdo: BDO | undefined) => {
		if (bdo === undefined) {
			return;
		}

		onChange({
			...value,
			bdo,
		});
	};

	const onWasteTypeChange = (wasteType: WasteType | undefined) => {
		if (wasteType === undefined) {
			return;
		}

		onChange({
			...value,
			wasteType,
		});
	};

	const onAddressChange = (address: string) => {
		const existing = addresses?.find((a) => a.name == address);

		onChange({
			...value,
			address,
			isNewAddress: existing === undefined,
		});
	};

	const onContactChange = (contact: string) => {
		onChange({
			...value,
			contact,
		});
	};

	const onOnPlaceChange = (onPlace: boolean) => {
		onChange({
			...value,
			onPlace,
		});
	};

	const onCountChange = (count: string) => {
		onChange({
			...value,
			count,
		});
	};

	const onClaimingDateChange = (date: moment.Moment | null) => {
		if (!date) {
			return;
		}

		const claimingAt = value.claimingDate?.clone() ?? moment().hours(0).minutes(0).seconds(0);

		onChange({
			...value,
			claimingDate: claimingAt.set({
				year: date.year(),
				month: date.month(),
				date: date.date(),
			}),
		});
	};

	const onClaimingTimeChange = (claimingTime: moment.Moment | null) => {
		if (!claimingTime) {
			return;
		}

		const claimingAt = value.claimingDate?.clone() ?? moment();

		onChange({
			...value,
			claimingDate: claimingAt.set({
				hours: claimingTime.hours(),
				minutes: claimingTime.minutes(),
			}),
		});
	};

	const onDeliveryDateChange = (date: moment.Moment | null) => {
		if (!date) {
			return;
		}

		const deliveryAt = value.deliveryDate?.clone() ?? moment().hours(0).minutes(0).seconds(0);

		onChange({
			...value,
			deliveryDate: deliveryAt.set({
				year: date.year(),
				month: date.month(),
				date: date.date(),
			}),
		});
	};

	const onDeliveryTimeChange = (deliveryTime: moment.Moment | null) => {
		if (!deliveryTime) {
			return;
		}

		const deliveryAt = value.deliveryDate?.clone() ?? moment();

		onChange({
			...value,
			deliveryDate: deliveryAt.set({
				hours: deliveryTime.hours(),
				minutes: deliveryTime.minutes(),
			}),
		});
	};

	const onPriceChange = (price: string) => {
		onChange({
			...value,
			price,
		});
	};

	const onPriceTypeChange = (priceType: PriceType | undefined) => {
		if (priceType === undefined) {
			return;
		}

		onChange({
			...value,
			priceType,
		});
	};

	const onPaymentTypeChange = (wantedPaymentType: PaymentType | undefined) => {
		if (wantedPaymentType === undefined) {
			return;
		}

		onChange({
			...value,
			wantedPaymentType,
		});
	};

	const onWantedAdvanceChange = (wantedAdvance: string | null) => {
		if (!wantedAdvance || wantedAdvance.trim() === '') {
			wantedAdvance = null;
		}

		onChange({
			...value,
			wantedAdvance,
		});
	};

	const onCommentsChange = (comments: string) => {
		onChange({
			...value,
			comments,
		});
	};

	return (
		<PageSection title="Klient" select={select}>
			<div className="new-delivery">
				<Space size={25}>
					<div className="flex flex-dir">
						<label>Klient</label>
						<SearchSelectorNew
							placeholder="Wybierz klienta"
							id="id"
							elements={clients}
							buildElement={(client) => client.name}
							value={value.client?.id ?? null}
							onChange={onClientChange}
							search={(inputValue, client) => {
								const search = inputValue.toLocaleLowerCase();

								if (client.name.toLocaleLowerCase().includes(search)) {
									return true;
								}

								if (client.phone.toLocaleLowerCase().includes(search)) {
									return true;
								}

								if (client.nip?.toLocaleLowerCase().includes(search)) {
									return true;
								}

								if (client.bdo?.toLocaleLowerCase().includes(search)) {
									return true;
								}

								if (client.email?.toLocaleLowerCase().includes(search)) {
									return true;
								}

								return false;
							}}
						/>
					</div>
					<div className="flex flex-dir flex-ac">
						<label>&nbsp;</label>
						<Button
							size="large"
							type="primary"
							icon={<PlusCircleOutlined />}
							onClick={async () => {
								const client = await pushWithResult(navigate, `/panel/new-client`);
								if (!client) {
									return;
								}

								const encoded = JSON.stringify(client);
								navigate(`.?type=3&client=${btoa(encoded)}`, { replace: true });
							}}
						>
							Dodaj nowego klienta
						</Button>
					</div>
				</Space>

				{value.client && <ClientInfo client={value.client} />}

				<PageSection title="Informacje">
					<Space size={25}>
						<div className="flex flex-dir">
							<label>BDO</label>
							<Select size="large" value={value.bdo ?? null} onChange={onBdoChange}>
								<Select.Option key="0" value={BDO.BDO}>
									BDO
								</Select.Option>
								<Select.Option key="1" value={BDO.producing}>
									Wytwarzający
								</Select.Option>
							</Select>
						</div>

						<div className="flex flex-dir">
							<label>Rodzaj odpadów</label>
							<AutoComplete
								size="large"
								options={wasteTypes.map((type) => ({ value: type }))}
								filterOption={(inputValue, option) =>
									option?.value.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase()) ?? false
								}
								value={value.wasteType ?? null}
								onSelect={onWasteTypeChange}
								onChange={onWasteTypeChange}
							/>
						</div>
					</Space>

					<Space size={25}>
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							<label>Adres</label>
							<SearchSelector
								id="name"
								elements={addresses}
								buildElement={(address) => (typeof address === 'string' ? address : address.name)}
								value={value.address}
								onChange={onAddressChange}
								allowCustom
							/>
						</div>
						<div>
							<label>Numer kontaktowy</label>
							<Input size="large" type="text" value={value.contact} onChange={(e) => onContactChange(e.target.value)} />
						</div>
					</Space>
				</PageSection>

				<PageSection title="Zapotrzebowanie">
					<Space size={25}>
						<Checkbox checked={!!value.onPlace} onChange={(e) => onOnPlaceChange(e.target.checked)}>
							Załadunek na miejscu
						</Checkbox>
						<div>
							<label>Ilość worków</label>
							<Input
								size="large"
								type="number"
								min={1}
								max={255}
								value={value.count}
								onChange={(e) => onCountChange(e.target.value)}
							/>
						</div>
					</Space>
					<Space size={25}>
						<div className="flex flex-dir">
							<label>{value.onPlace ? 'Termin realizacji' : 'Termin podstawienia'}</label>
							<DatePicker
								size="large"
								placeholder="Wybierz datę"
								value={value.deliveryDate ?? null}
								onChange={onDeliveryDateChange}
							/>
						</div>
						<div>
							<label>&nbsp;</label>
							<DatePicker.TimePicker
								size="large"
								format="HH:mm"
								placeholder="Wybierz godzinę"
								value={value.deliveryDate?.hours() === 0 ? null : value.deliveryDate ?? null}
								onChange={onDeliveryTimeChange}
							/>
						</div>
					</Space>
					{!value.onPlace && (
						<Space size={25}>
							<div className="flex flex-dir">
								<label>Termin odbioru</label>
								<DatePicker
									size="large"
									placeholder="Wybierz datę"
									value={value.claimingDate ?? null}
									onChange={onClaimingDateChange}
								/>
							</div>
							<div>
								<label>&nbsp;</label>
								<DatePicker.TimePicker
									size="large"
									format="HH:mm"
									placeholder="Wybierz godzinę"
									value={value.claimingDate?.hours() === 0 ? null : value.claimingDate ?? null}
									onChange={onClaimingTimeChange}
								/>
							</div>
						</Space>
					)}

					<Space size={25}>
						<div>
							<label>Cena</label>
							<div className="flex">
								<Input size="large" type="text" value={value.price} onChange={(e) => onPriceChange(e.target.value)} />
								<span className="pln flex flex-dir flex-jc flex-ac">PLN</span>
							</div>
						</div>
						<div className="flex flex-dir">
							<label>Netto / Brutto</label>
							<Select size="large" value={value.priceType ?? null} onChange={onPriceTypeChange}>
								<Select.Option key="0" value={PriceType.net}>
									Netto
								</Select.Option>
								<Select.Option key="1" value={PriceType.gross}>
									Brutto
								</Select.Option>
							</Select>
						</div>
					</Space>

					<Space size={25}>
						<div className="flex flex-dir">
							<label>Typ zapłaty</label>
							<Select size="large" value={value.wantedPaymentType ?? null} onChange={onPaymentTypeChange}>
								<Select.Option key="0" value={PaymentType.transfer}>
									Przelew
								</Select.Option>
								<Select.Option key="1" value={PaymentType.cash}>
									Gotówka
								</Select.Option>
							</Select>
						</div>
						<div>
							<label>Zaliczka</label>
							<div className="flex gap-x-4">
								<Input
									size="large"
									type="text"
									value={value.wantedAdvance ?? ''}
									onChange={(e) => onWantedAdvanceChange(e.target.value)}
								/>
								<Button type="primary" size="large" onClick={() => onWantedAdvanceChange(null)}>
									<CloseCircleOutlined />
								</Button>
							</div>
						</div>
					</Space>
				</PageSection>

				<PageSection title="Uwagi">
					<Input.TextArea value={value.comments} onChange={(e) => onCommentsChange(e.target.value)} />
				</PageSection>
			</div>
		</PageSection>
	);
};
